import orderBy from "lodash/orderBy";

// Ported from MotoList.js. Keep behavior identical: this decides which
// local / European offers customers see and in what order.

export function getStock(product) {
  const stock =
    typeof product?.stock === "number"
      ? product.stock
      : parseInt(product?.stock, 10) || 0;
  const feedQuantity =
    typeof product?.feedQuantity === "number"
      ? product.feedQuantity
      : parseInt(product?.feedQuantity, 10) || 0;

  return Math.max(stock, feedQuantity);
}

export function isEuropeanStockProduct(product) {
  return product?.source === "georgia" || product?.visibility === "ondemand";
}

export function isLegacyOnDemandProduct(product) {
  return (
    product?.visibility === "ondemand" &&
    product?.source !== "georgia" &&
    !product?.georgiaMeta
  );
}

export function isLocalStockProduct(product) {
  return (
    !isEuropeanStockProduct(product) &&
    product?.visibility === "instock" &&
    getStock(product) > 0
  );
}

export function isLocalOffer(product) {
  if (isEuropeanStockProduct(product)) return false;
  if (product?.visibility === "preorder") return true;
  return isLocalStockProduct(product);
}

export function isOutOfStock(product) {
  const visibility = product?.visibility;
  return visibility === "outofstock" || visibility === "notinstock";
}

export function getSetComponentPairKey(product) {
  if (!product?.set || !product?.eans?.front || !product?.eans?.rear) {
    return null;
  }

  return `${String(product.eans.front).trim()}|${String(product.eans.rear).trim()}`;
}

export function normalizeSetText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/\b(tl|tt)\b/g, " ")
    .replace(/[^a-z0-9]+/g, "")
    .trim();
}

export function normalizeSetModel(value, brand) {
  const model = normalizeSetText(value);
  const normalizedBrand = normalizeSetText(brand);

  if (normalizedBrand === "continental") {
    return model.replace(/^(continental|conti)/, "");
  }

  return model;
}

export function extractTireSizeKeys(displaySize) {
  const matches = String(displaySize || "").matchAll(
    /(\d{2,3})\s*\/\s*(\d{2,3})\s*(?:[a-z\s-]*?)(\d{2})/gi
  );

  return Array.from(matches, (match) => `${match[1]}/${match[2]}R${match[3]}`);
}

export function getSetIdentityKey(product) {
  if (!product?.set) return null;

  const brand = normalizeSetText(product.brand);
  const model = normalizeSetModel(product.displayName || product.name, product.brand);
  const sizeKeys = extractTireSizeKeys(product.displaySize).sort();

  if (!brand || !model || sizeKeys.length < 2) return null;

  return `${brand}|${model}|${sizeKeys.join("+")}`;
}

export function getProductEans(product = {}) {
  return [product.ean, product.eans?.front, product.eans?.rear]
    .filter(Boolean)
    .map((ean) => String(ean));
}

export function getSingleIdentityKey(product = {}) {
  if (!product || product.set) return null;
  const brand = normalizeSetText(product.brand);
  const model = normalizeSetModel(product.displayName || product.name, product.brand);
  const width = String(product.width ?? "").trim();
  const rim = String(product.rim ?? "").trim();
  if (!brand || !model || !width || !rim) return null;
  const profile = String(product.profile ?? "").trim() || "-";
  return `${brand}|${model}|${width}/${profile}/${rim}`;
}

export function getSetComponentIdentityKeys(product = {}) {
  if (!product?.set) return [];
  const brand = normalizeSetText(product.brand);
  const model = normalizeSetModel(product.displayName || product.name, product.brand);
  if (!brand || !model) return [];
  return extractTireSizeKeys(product.displaySize)
    .map((sizeKey) => {
      const match = String(sizeKey).match(/^(\d+)\/(\d+)R(\d+)$/i);
      if (!match) return null;
      return `${brand}|${model}|${match[1]}/${match[2]}/${match[3]}`;
    })
    .filter(Boolean);
}

export function isCoveredBy(product, cover) {
  if (!product || !cover) return false;
  const productEans = new Set(getProductEans(product));
  if (getProductEans(cover).some((ean) => productEans.has(ean))) return true;

  const pairKey = getSetComponentPairKey(product);
  if (pairKey && pairKey === getSetComponentPairKey(cover)) return true;

  const setIdentity = getSetIdentityKey(product);
  if (setIdentity && setIdentity === getSetIdentityKey(cover)) return true;

  const singleIdentity = getSingleIdentityKey(product);
  if (singleIdentity && singleIdentity === getSingleIdentityKey(cover)) return true;

  return false;
}

/**
 * Local in-stock and dated preorder win. Hide the European card, and hide
 * a local card that is no longer buyable when that European card remains.
 */
export function applyLocalEuropeanVisibility(tires) {
  if (!Array.isArray(tires) || tires.length === 0) return [];

  const localOffers = tires.filter((product) => isLocalOffer(product));
  const localSingleKeys = new Set(
    localOffers.map((product) => getSingleIdentityKey(product)).filter(Boolean)
  );

  const withoutCoveredEuropean = tires.filter((product) => {
    if (!isEuropeanStockProduct(product) || isLegacyOnDemandProduct(product)) {
      return !isLegacyOnDemandProduct(product);
    }
    if (localOffers.some((offer) => isCoveredBy(product, offer))) return false;
    const componentKeys = getSetComponentIdentityKeys(product);
    return !(
      componentKeys.length >= 2 &&
      componentKeys.every((key) => localSingleKeys.has(key))
    );
  });

  const europeanLeft = withoutCoveredEuropean.filter((product) =>
    isEuropeanStockProduct(product)
  );

  return withoutCoveredEuropean.filter((product) => {
    if (isEuropeanStockProduct(product) || isLocalOffer(product)) return true;
    return !europeanLeft.some((european) => isCoveredBy(product, european));
  });
}

// MotoList rendered these visibility groups in this order. Products with a
// visibility value outside the groups were never rendered.
export function getVisibilityRank(product) {
  if (!product) return null;
  if (!("visibility" in product)) return 4;

  const visibility = product.visibility;
  if (visibility === "instock") return 0;
  if (visibility === "preorder") return 1;
  if (visibility === "ondemand") return 2;
  if (isOutOfStock(product)) return 3;
  return null;
}

export function prepareListingProducts(tires) {
  return applyLocalEuropeanVisibility(tires).filter(
    (product) => !isLegacyOnDemandProduct(product) && getVisibilityRank(product) !== null
  );
}

export function getLimitedOfferEanSet(limitedTimeOffers) {
  return new Set(
    (Array.isArray(limitedTimeOffers) ? limitedTimeOffers : [])
      .map((offer) => offer?.ean)
      .filter((ean) => ean != null && ean !== "")
      .map(String)
  );
}

export function findLimitedOffer(product, limitedTimeOffers) {
  if (!product || product.ean == null || !Array.isArray(limitedTimeOffers)) return null;
  const ean = String(product.ean);
  return limitedTimeOffers.find((offer) => offer && offer.ean != null && String(offer.ean) === ean) || null;
}

export function isPromoted(product, limitedOfferEanSet) {
  const hasOffer = !!product?.ean && !!limitedOfferEanSet?.has(String(product.ean));
  return hasOffer && !isOutOfStock(product);
}

// Equivalent to MotoList's nesting: promoted partition → visibility group →
// sets before singles → price ascending. orderBy is stable, so equal keys keep API order.
export function sortRecommended(products, limitedOfferEanSet) {
  return orderBy(
    Array.isArray(products) ? products : [],
    [
      (product) => (isPromoted(product, limitedOfferEanSet) ? 0 : 1),
      (product) => {
        const rank = getVisibilityRank(product);
        return rank === null ? 5 : rank;
      },
      (product) => (product?.set ? 0 : 1),
      (product) => Number(product?.price) || 0,
    ],
    ["asc", "asc", "asc", "asc"]
  );
}
